import type { ShortsEntrySource } from './entrySource';
import type { FeedPage } from '~/composables/shorts/useFetchShorts';
import type { PrefetchVariant } from '~/core/entities/property';
import type { MaybeRefOrGetter } from 'vue';

import { useLastVideoState } from '~/composables/shorts/useLastVideoState';
import { usePropertyIntentPrefetch } from '~/composables/shorts/usePropertyIntentPrefetch';
import { useShortsFeedHandoff } from '~/composables/shorts/useShortsFeedHandoff';
import { useFeatureFlags } from '~/composables/useFeatureFlags';
import { shortsFiltersToListingQuery } from '~/composables/useShortsFilters/featuredVideosFilterRules';
import { markShortsOpenIntent } from '~/composables/useVideoQoE';
import {
	prefetchShakaChunkOnIdle,
	prefetchVideoOnIntent
} from '~/composables/useVideoWarmup';

import {
	blessSharedVideoElement,
	setIOSMsePreference
} from '~/components/PropertyReelsFeed/useIOSSharedVideo';

import { withEntrySource } from './entrySource';

export { SHORTS_ENTRY_SOURCE, type ShortsEntrySource } from './entrySource';

export interface OpenShortsFeedParams {
	/** Imóvel que deve abrir, encontrado por id — nunca por índice persistido. */
	at: string;
	/** Lista pronta da origem. Ausente apenas em entradas sem catálogo local. */
	page?: FeedPage | null;
	queryParams?: Record<string, string>;
	entrySource?: ShortsEntrySource;
	/** Mídia conhecida por uma origem sem `FeedPage`, como o catálogo de clusters. */
	intentVideo?: {
		url?: string | null;
		prefetchVariants?: PrefetchVariant[] | null;
	};
}

export function buildShortsFeedDestination(
	commercialId: string,
	query: Record<string, string> = {},
	entrySource?: ShortsEntrySource
): string {
	const search = new URLSearchParams(
		withEntrySource(query, entrySource)
	).toString();
	return `/shorts/${commercialId}${search ? `?${search}` : ''}`;
}

export interface UseOpenShortsFeedOptions {
	/**
	 * Condição para aquecer o chunk do player em idle. Uma origem cujo gatilho
	 * pode nunca ser renderizado — as seções do perfil do corretor só aparecem
	 * quando há conteúdo — passa a própria condição de render, para que a visita
	 * que nunca abre o feed não pague o download do player. Omitida, aquece já.
	 */
	shouldWarmPlayerChunk?: MaybeRefOrGetter<boolean>;
}

export function useOpenShortsFeed(options: UseOpenShortsFeedOptions = {}) {
	const lastVideoState = useLastVideoState();
	const handoff = useShortsFeedHandoff();

	setIOSMsePreference(useFeatureFlags().ft_shorts_ios_mse);

	watch(
		() => toValue(options.shouldWarmPlayerChunk ?? true),
		shouldWarm => {
			if (shouldWarm) prefetchShakaChunkOnIdle();
		},
		{ immediate: true }
	);

	const { prefetchPropertyOnIntent } = usePropertyIntentPrefetch();

	function openShortsFeed({
		at,
		page,
		queryParams,
		entrySource,
		intentVideo
	}: OpenShortsFeedParams): void {
		const path = `/shorts/${at}`;
		const targetIndex =
			page?.items.findIndex(item => item.property.commercialId === at) ?? -1;
		const targetItem = targetIndex >= 0 ? page?.items[targetIndex] : undefined;
		const targetVideo = targetItem?.kind === 'video' ? targetItem : undefined;
		const videoUrl = targetVideo?.videoUrl ?? intentVideo?.url;
		const prefetchVariants =
			targetVideo?.prefetchVariants ??
			intentVideo?.prefetchVariants ??
			undefined;

		markShortsOpenIntent();
		prefetchPropertyOnIntent(at);
		prefetchVideoOnIntent(videoUrl, prefetchVariants);
		lastVideoState.save(
			at,
			Math.max(targetIndex, 0),
			videoUrl,
			prefetchVariants
		);
		blessSharedVideoElement();

		// Evita que uma origem sem página reutilize uma entrega anterior.
		handoff.value = page ? { page, at } : null;

		if (queryParams) {
			navigateTo({ path, query: withEntrySource(queryParams, entrySource) });
			return;
		}

		const filters = page?.cursor.filters;
		if (!filters) {
			navigateTo(
				entrySource ? { path, query: withEntrySource({}, entrySource) } : path
			);
			return;
		}

		navigateTo({
			path,
			query: withEntrySource(shortsFiltersToListingQuery(filters), entrySource)
		});
	}

	return { openShortsFeed };
}
